import React, { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { getDealRoomBrandedName } from '../../utils/locale';
import SingleColumnTemplate from '../../../../ui/layouts/SingleColumnTemplate.jsx';
import { gql, useQuery, useMutation } from '@apollo/client';
import apolloClient from '../../lib/apolloClient';
import Card from '../../../../ui/canon/Card';
import Button from '../../../../ui/canon/Button';
import GettingStartedMiniCard from '../GettingStarted/GettingStartedMiniCard';
import GettingStartedStepCard from '../GettingStarted/GettingStartedStepCard';
import useGettingStartedData, { MARKETPLACE_VISIBILITY_ID } from '../GettingStarted/useGettingStartedData';
import RjVisibilityFirstRunModal from './RjVisibility/RjVisibilityFirstRunModal';
import RjVisibilitySheet from './RjVisibility/RjVisibilitySheet';
import AssistantQuickAsk from '../../assistant/components/AssistantQuickAsk.jsx';
import SellerHeatMapLaunchpadContainer from '../../containers/SellerHeatMapLaunchpadContainer';
import ProgramCard from '../coachLed/ProgramCard.jsx';
import GuidedTour from '../Shared/GuidedTour/GuidedTour';
import ValuationSnapshot from './ValuationSnapshot';
import MultiLineValuationSnapshot from './MultiLineValuationSnapshot';
import DealRoomMetricsCard from './DealRoomMetricsCard';
import BuyerDealInterestsCard from './BuyerDealInterestsCard';
import { PlanStatusTile } from './PlanStatusTile';
import ProfileCompletionCard from './ProfileCompletionCard';
import QuickstartWelcomeMessage from './QuickstartWelcomeMessage';
import ReadinessAssessmentPrompt from './ReadinessAssessmentPrompt';
import ReadinessScoreCard from './ReadinessScoreCard';
import ReadinessAssessmentFlow from '../PlanBuilder/pages/ReadinessAssessmentFlow';
import { Anchor, TrendingUp, Zap, GraduationCap, Rocket, CheckCircle2, ArrowRight as ArrowRightIcon, X as XIcon, ShieldCheck as ShieldCheckIcon, AlertTriangle as AlertTriangleIcon, Shield as ShieldIcon, Clock as ClockIcon, Play as PlayIcon } from 'lucide-react';
import { PlayCircleIcon as PlayCircleOutlineIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import { useSuccessionTheme } from './SuccessionThemeProvider';
import SuccessionMarketplaceTicker from './SuccessionMarketplaceTicker';
import WelcomeVideoDialog from './WelcomeVideoDialog';

// Welcome-video integration — feature flag `launchpad_show_welcome_video` enables it,
// per-user "watched" state is persisted in localStorage (matches TOUR_STORAGE_KEY_PREFIX).
const VIDEO_TASK_ID = 'watch-intro-video';
const VIDEO_WATCHED_KEY_PREFIX = 'launchpad_welcome_video_watched_v1_';

const RESTART_READINESS_ASSESSMENT = gql`
  mutation RestartReadinessAssessment {
    restartReadinessAssessment {
      userPlanProgress {
        id
        status
      }
      errors
    }
  }
`;

// Returns the recomputed statuses for the same result id, so Apollo updates the
// score card straight from the cache — no refetch, and the ring and the row can
// never briefly disagree.
const TOGGLE_READINESS_AREA_REVIEW = gql`
  mutation ToggleReadinessAreaReview($category: String!, $reviewed: Boolean!) {
    toggleReadinessAreaReview(category: $category, reviewed: $reviewed) {
      result {
        id
        categoryStatuses {
          category
          status
          inScope
        }
      }
      errors
    }
  }
`;

const REQUEST_CAT_PLAN_EMAIL = gql`
  mutation RequestCatPlanEmail {
    request_cat_plan_email {
      success
      message
    }
  }
`;

// GraphQL query for fetching succession plan status + readiness assessment state
const GET_SUCCESSION_PLAN_STATUS = gql`
  query GetSuccessionPlanStatus {
    getCurrentUser {
      id
      motivation
      approval_state
      pending_motivation
      has_legacy_catastrophic_plan
      legacy_catastrophic_plan_status
      # RJ Seller Visibility Workflow — drives the marketplace_visibility
      # quickstart card and the first-run modal suppression gate. Read live
      # rather than from server props so an inline toggle reflects without a
      # page reload.
      marketplace_visibility_opt_in
      rj_visibility_first_run_dismissed_at
      minimum_profile_completed
      modernized_onboarding_completed
      company {
        id
        features
      }
    }
    readinessAssessmentResult {
      id
      categoryScores
      categoryStatuses {
        category
        status
        inScope
      }
      overallScore
      questionSet
      skipped
      completed
      pending
      suggestedActivityIds
      lowScoreCategories
      completedAt
      furtherReading {
        category
        links {
          title
          url
          description
        }
      }
      allResourcesHtml
    }
    showReadinessAssessmentPrompt
    successionReadinessPlanSlug
    planTemplates {
      id
      name
      slug
      planType
      description
      ceCreditHours
      ceEligible
      planModules {
        id
        planSections {
          id
          planQuestions {
            id
          }
        }
      }
    }
    mySuccessionPlans {
      id
      status
      completionPercentage
      updatedAt
      completedAt
      ceCreditEarned
      userPlanResponses {
        id
      }
      planTemplate {
        id
        slug
        planType
      }
    }
  }
`;

const FEATURED_PROGRAMS_QUERY = gql`
  query FeaturedSprintPrograms {
    sprintPrograms(status: "published", featured: true, limit: 3) {
      id
      title
      slug
      summary
      level
      price_cents
      featured
      hero_image_url
      duration_days
      is_group
      coachProfile {
        id
        name
        headline
        avatar_url
        verified
        rating
        review_count
      }
    }
  }
`;

// Approval Status Banner — shown when tenant has marketplace approval enabled
const APPROVED_BANNER_DISMISSED_KEY_PREFIX = 'marketplace_approved_banner_dismissed_v1_';

const ApprovalStatusBanner = ({ approvalState, motivation, pendingMotivation, features = [], userId, reviewerLabel = 'our team' }) => {
  const intl = useIntl();
  const hasApprovalProcess = features.includes('successor_approval_process') || features.includes('seller_approval_process');

  // Normalize: approval_state can be a string or integer
  const isPending = approvalState === 'pending' || approvalState === 0;
  const isDenied = approvalState === 'denied' || approvalState === 2;
  const isApproved = approvalState === 'approved' || approvalState === 1;

  const approvedDismissKey = userId ? `${APPROVED_BANNER_DISMISSED_KEY_PREFIX}${userId}` : null;
  const [approvedDismissed, setApprovedDismissed] = React.useState(() => {
    if (typeof window === 'undefined' || !approvedDismissKey) return false;
    try {
      return window.localStorage.getItem(approvedDismissKey) === '1';
    } catch (e) {
      return false;
    }
  });

  if (!hasApprovalProcess) return null;
  if (!isPending && !isDenied && !isApproved) return null;
  // Users fully opted out (no_motivation AND no pending change) aren't marketplace
  // participants; the banner is meaningless for them. But if they're no_motivation
  // with a pending change to growth/succession, they ARE in flight and need to see
  // the pending banner.
  if (motivation === 'no_motivation' && !pendingMotivation) return null;

  if (isPending) {
    return (
      <div className="mt-4 rounded-lg border border-succession-warning bg-succession-warning/10 px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-succession-warning/15">
            <ClockIcon className="h-4.5 w-4.5 text-succession-warning" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-succession-text-primary leading-tight">
              {intl.formatMessage({ id: 'succession.launchpad.approval_banner_pending_title' })}
            </p>
            <p className="mt-1 text-sm text-succession-text-secondary leading-snug">
              {intl.formatMessage({ id: 'succession.launchpad.approval_banner_pending_body' }, { reviewer: reviewerLabel })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isApproved) {
    // Rejection Policy "preserved-approval" variant: when a user is approved BUT also
    // has a pending_motivation, that means they had a change request which was rejected
    // (the intent never got applied). Show the denial copy with "marketplace access is
    // unchanged" reassurance instead of the plain approved banner.
    if (pendingMotivation) {
      const motivationLabelLocal = (m) =>
        m === 'growth' ? intl.formatMessage({ id: 'succession.launchpad.persona_buyer', defaultMessage: 'Buyer' }) :
        m === 'succession' ? intl.formatMessage({ id: 'succession.launchpad.persona_seller', defaultMessage: 'Seller' }) :
        intl.formatMessage({ id: 'succession.launchpad.persona_researcher', defaultMessage: 'Researcher' });
      return (
        <div className="mt-4 rounded-lg border border-succession-border bg-succession-bg-muted/40 px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-succession-bg-muted">
              <ShieldIcon className="h-4.5 w-4.5 text-succession-text-secondary" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-succession-text-primary leading-tight">
                {intl.formatMessage(
                  { id: 'succession.launchpad.approval_banner_denied_change_title', defaultMessage: 'Your request to change to {target} was not approved' },
                  { target: motivationLabelLocal(pendingMotivation) }
                )}
              </p>
              <p className="mt-1 text-sm text-succession-text-secondary leading-snug">
                {intl.formatMessage(
                  { id: 'succession.launchpad.approval_banner_denied_change_body', defaultMessage: 'Your motivation remains {current} and your marketplace access is unchanged.' },
                  { current: motivationLabelLocal(motivation) }
                )}
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (approvedDismissed) return null;
    const dismiss = () => {
      if (approvedDismissKey) {
        try { window.localStorage.setItem(approvedDismissKey, '1'); } catch (e) { /* noop */ }
      }
      setApprovedDismissed(true);
    };
    return (
      <div className="mt-4 rounded-lg border border-succession-success bg-succession-success/10 px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-succession-success/15">
            <CheckCircle2 className="h-4.5 w-4.5 text-succession-success" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-succession-text-primary leading-tight">
              {intl.formatMessage({ id: 'succession.launchpad.approval_banner_approved_title' })}
            </p>
            <p className="mt-1 text-sm text-succession-text-secondary leading-snug">
              {intl.formatMessage({ id: 'succession.launchpad.approval_banner_approved_body' })}
            </p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="shrink-0 text-succession-text-secondary hover:text-succession-text-primary transition-colors"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // Denied — context-aware variants per the Rejection Policy.
  // - First-time rejection (motivation = no_motivation): "You don't have marketplace access."
  // - Previously-approved user with rejected change request: "Your request to change to X
  //   was not approved. Your motivation remains Y and your marketplace access is unchanged."
  const motivationLabel = (m) =>
    m === 'growth' ? intl.formatMessage({ id: 'succession.launchpad.persona_buyer', defaultMessage: 'Buyer' }) :
    m === 'succession' ? intl.formatMessage({ id: 'succession.launchpad.persona_seller', defaultMessage: 'Seller' }) :
    intl.formatMessage({ id: 'succession.launchpad.persona_researcher', defaultMessage: 'Researcher' });

  const isFirstTimeRejection = motivation === 'no_motivation' || !motivation;
  const deniedTitle = isFirstTimeRejection
    ? intl.formatMessage({ id: 'succession.launchpad.approval_banner_denied_first_time_title', defaultMessage: 'Your request was not approved' })
    : intl.formatMessage(
        { id: 'succession.launchpad.approval_banner_denied_change_title', defaultMessage: 'Your request to change to {target} was not approved' },
        { target: motivationLabel(pendingMotivation) }
      );
  const deniedBody = isFirstTimeRejection
    ? intl.formatMessage({ id: 'succession.launchpad.approval_banner_denied_first_time_body', defaultMessage: "You don't have marketplace access. To request re-review, please update your profile and contact support." })
    : intl.formatMessage(
        {
          id: 'succession.launchpad.approval_banner_denied_change_body',
          defaultMessage: 'Your motivation remains {current} and your marketplace access is unchanged.',
        },
        { current: motivationLabel(motivation) }
      );

  return (
    <div className="mt-4 rounded-lg border border-succession-border bg-succession-bg-muted/40 px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-succession-bg-muted">
          <ShieldIcon className="h-4.5 w-4.5 text-succession-text-secondary" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-succession-text-primary leading-tight">
            {deniedTitle}
          </p>
          <p className="mt-1 text-sm text-succession-text-secondary leading-snug">
            {deniedBody}
            {' '}
            <a
              href="/succession/profile"
              className="font-medium text-succession-primary underline hover:no-underline"
            >
              {intl.formatMessage({ id: 'succession.launchpad.approval_banner_denied_body_link' })}
            </a>
            {' '}
            {intl.formatMessage({ id: 'succession.launchpad.approval_banner_denied_body_suffix' })}
          </p>
        </div>
      </div>
    </div>
  );
};

const TOUR_STORAGE_KEY_PREFIX = 'launchpad_tour_shown_v1_';

// Getting Started Progress Widget - shows completion progress
const GettingStartedProgress = ({ completedCount, totalCount, className = '' }) => {
  const intl = useIntl();
  const percentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className={`bg-succession-bg-primary border border-succession-border rounded-lg p-6 shadow-succession-md hover:shadow-succession-lg transition-shadow ${className}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-succession-text-primary mb-1">{intl.formatMessage({ id: 'succession.launchpad.getting_started' })}</h3>
          <a
            href="#quickstart-guide"
            className="inline-flex items-center text-sm font-medium text-succession-primary hover:text-succession-primary-hover transition-colors group"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('quickstart-guide')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            {intl.formatMessage({ id: 'succession.launchpad.tasks_completed' }, { completed: completedCount, total: totalCount })}
            <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
        <div className="flex items-center gap-2 text-succession-success">
          <CheckCircle2 className="w-5 h-5" />
          <span className="text-2xl font-bold">{percentage}%</span>
        </div>
      </div>
      {/* Progress bar */}
      <div className="h-2 bg-succession-bg-secondary rounded-full overflow-hidden">
        <div 
          className="h-full bg-succession-success rounded-full transition-all duration-500 ease-out"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Self-contained Succession Plan Widget - fetches actual plan data
const SuccessionPlanWidget = ({ showHeaderIcon = false }) => {
  const { data, loading, error } = useQuery(GET_SUCCESSION_PLAN_STATUS, { fetchPolicy: 'cache-first', client: apolloClient });
  
  if (loading) {
    return (
      <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border p-6 h-full animate-pulse">
        <div className="flex items-start gap-4">
          <div className="flex-1 space-y-2">
            <div className="h-5 bg-succession-bg-secondary rounded w-32" />
            <div className="h-4 bg-succession-bg-secondary rounded w-48" />
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return null; // Silently fail - widget is optional
  }
  
  const templates = data?.planTemplates || [];
  const userPlans = data?.mySuccessionPlans || [];
  const currentUser = data?.getCurrentUser;
  
  // Check if plan_builder_ce feature is enabled
  const features = currentUser?.company?.features || [];
  const ceFeatureEnabled = features.includes('plan_builder_ce');
  
  // Find succession-type template
  const successionTemplate = templates.find((t) => 
    t.planType === 'succession' || 
    t.slug === 'comprehensive-succession-plan-v2' || 
    t.slug === 'comprehensive-succession-plan'
  );
  
  if (!successionTemplate) return null;
  
  // Find user's plan for this template
  const userPlan = userPlans.find((p) => p.planTemplate?.id === successionTemplate.id);
  
  // Calculate total questions
  const getTotalQuestions = (template) => {
    if (!template?.planModules) return 0;
    return template.planModules.reduce((modAcc, mod) => {
      return modAcc + (mod.planSections?.reduce((secAcc, sec) => {
        return secAcc + (sec.planQuestions?.length || 0);
      }, 0) || 0);
    }, 0);
  };
  
  // Determine status
  const getStatus = (progress) => {
    if (!progress) return 'empty';
    if (progress.status === 'completed') return 'completed';
    if (progress.status === 'in_progress' || progress.status === 'paused') return 'in_progress';
    return 'empty';
  };
  
  const status = getStatus(userPlan);
  const totalQuestions = getTotalQuestions(successionTemplate);
  const questionsAnswered = userPlan?.userPlanResponses?.length || 0;
  const completionPct = userPlan?.completionPercentage || 0;
  const lastActivity = userPlan?.updatedAt || userPlan?.completedAt;
  
  // Build CE credit info if feature is enabled and template has CE
  let ceCredit = null;
  if (ceFeatureEnabled && successionTemplate.ceEligible) {
    ceCredit = {
      available: true,
      earned: userPlan?.ceCreditEarned || false,
      creditHours: successionTemplate.ceCreditHours || 1,
    };
  }
  
  return (
    <PlanStatusTile
      type="succession"
      templateName={successionTemplate.name}
      templateDescription={successionTemplate.description}
      status={status}
      progress={completionPct}
      questionsAnswered={questionsAnswered}
      totalQuestions={totalQuestions}
      lastActivity={lastActivity}
      planSlug={successionTemplate.slug}
      ceCredit={ceCredit}
      showHeaderIcon={showHeaderIcon}
    />
  );
};

// Self-contained Catastrophic Plan Widget - fetches actual plan data
const CatastrophicPlanWidget = ({ showHeaderIcon = false }) => {
  const { data, loading, error } = useQuery(GET_SUCCESSION_PLAN_STATUS, { fetchPolicy: 'cache-first', client: apolloClient });
  
  if (loading) {
    return (
      <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border p-6 h-full animate-pulse">
        <div className="flex items-start gap-4">
          <div className="flex-1 space-y-2">
            <div className="h-5 bg-succession-bg-secondary rounded w-32" />
            <div className="h-4 bg-succession-bg-secondary rounded w-48" />
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return null; // Silently fail - widget is optional
  }
  
  const templates = data?.planTemplates || [];
  const userPlans = data?.mySuccessionPlans || [];
  const currentUser = data?.getCurrentUser;
  
  // Check if plan_builder_ce feature is enabled
  const features = currentUser?.company?.features || [];
  const ceFeatureEnabled = features.includes('plan_builder_ce');
  
  // Find catastrophic-type template
  const catastrophicTemplate = templates.find((t) => 
    t.planType === 'catastrophic' || 
    t.slug === 'catastrophic-plan-v1'
  );
  
  if (!catastrophicTemplate) return null;
  
  // Find user's plan for this template
  const userPlan = userPlans.find((p) => p.planTemplate?.id === catastrophicTemplate.id);
  
  // Only show if user has started the plan
  if (!userPlan) return null;
  
  // Calculate total questions
  const getTotalQuestions = (template) => {
    if (!template?.planModules) return 0;
    return template.planModules.reduce((modAcc, mod) => {
      return modAcc + (mod.planSections?.reduce((secAcc, sec) => {
        return secAcc + (sec.planQuestions?.length || 0);
      }, 0) || 0);
    }, 0);
  };
  
  // Determine status
  const getStatus = (progress) => {
    if (!progress) return 'empty';
    if (progress.status === 'completed') return 'completed';
    if (progress.status === 'in_progress' || progress.status === 'paused') return 'in_progress';
    return 'empty';
  };
  
  const status = getStatus(userPlan);
  const totalQuestions = getTotalQuestions(catastrophicTemplate);
  const questionsAnswered = userPlan?.userPlanResponses?.length || 0;
  const completionPct = userPlan?.completionPercentage || 0;
  const lastActivity = userPlan?.updatedAt || userPlan?.completedAt;
  
  // Build CE credit info if feature is enabled and template has CE
  let ceCredit = null;
  if (ceFeatureEnabled && catastrophicTemplate.ceEligible) {
    ceCredit = {
      available: true,
      earned: userPlan?.ceCreditEarned || false,
      creditHours: catastrophicTemplate.ceCreditHours || 1,
    };
  }
  
  return (
    <PlanStatusTile
      type="catastrophic"
      templateName={catastrophicTemplate.name}
      templateDescription={catastrophicTemplate.description}
      status={status}
      progress={completionPct}
      questionsAnswered={questionsAnswered}
      totalQuestions={totalQuestions}
      lastActivity={lastActivity}
      planSlug={catastrophicTemplate.slug}
      ceCredit={ceCredit}
      showHeaderIcon={showHeaderIcon}
    />
  );
};

// Pact-builder aware Catastrophic Plan widget — shown when continuity_builder feature is on
const CatastrophicPlanCBWidget = ({ workflowState, partnerStepCompleted = false, showHeaderIcon = false, isRj = false, planBuilderEnabled = true, searchOnly = false }) => {
  const { data, loading, error } = useQuery(GET_SUCCESSION_PLAN_STATUS, { fetchPolicy: 'cache-first', client: apolloClient });
  const [requestCatPlanEmail] = useMutation(REQUEST_CAT_PLAN_EMAIL, { client: apolloClient });
  const [emailSent, setEmailSent] = useState(false);

  if (loading) {
    return (
      <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border p-6 h-full animate-pulse">
        <div className="flex-1 space-y-2">
          <div className="h-5 bg-succession-bg-secondary rounded w-32" />
          <div className="h-4 bg-succession-bg-secondary rounded w-48" />
        </div>
      </div>
    );
  }

  if (error) return null;

  const userPlans = data?.mySuccessionPlans || [];
  const catPlan = userPlans.find((p) =>
    p.planTemplate?.planType === 'catastrophic' || p.planTemplate?.slug === 'catastrophic-plan-v1'
  );
  const hasPlan = !!(catPlan && (catPlan.status === 'in_progress' || catPlan.status === 'completed' || (catPlan.completionPercentage || 0) > 0));
  const completionPct = catPlan?.completionPercentage || 0;

  const isApproved    = workflowState === 'seal_issued';
  const isFiled       = workflowState === 'filed_agreement' || isApproved;
  const hasPartner    = partnerStepCompleted === true;

  const partnerLabel = searchOnly ? 'Search for Partner' : 'Register Partner';

  const stages = planBuilderEnabled
    ? [
      { label: 'Create Plan',       done: hasPlan },
      { label: partnerLabel,        done: hasPartner, isOptional: true },
      { label: 'File Agreement',    done: isFiled },
      { label: 'Approved',          done: isApproved },
    ]
    : [
      { label: partnerLabel,        done: hasPartner, isOptional: true },
      { label: 'File Agreement',    done: isFiled },
      { label: 'Approved',          done: isApproved },
    ];
  const completedCount = stages.filter((s) => s.done).length;
  const currentIdx = stages.findIndex((s) => !s.done);

  const statusLine = (() => {
    if (isApproved)                           return 'Your catastrophic plan is on file.';
    if (isFiled)                              return 'Agreement filed — awaiting approval';
    if (hasPartner && !isFiled)               return searchOnly ? 'Partner found — file your agreement next' : 'Partner registered — file your agreement next';
    if (planBuilderEnabled && hasPlan && !hasPartner) return `Plan started — ${searchOnly ? 'search for' : 'register'} your partner next`;
    if (!planBuilderEnabled && !hasPartner)           return searchOnly ? 'Search for your partner next' : 'Register your partner next';
    return `${completedCount} of ${stages.length} steps complete`;
  })();

  return (
    <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border p-6 hover:shadow-succession-lg transition-shadow flex flex-col h-full">
      {/* Header — matches PlanStatusTile exactly */}
      <div className="flex items-start justify-between mb-4">
        <div className={`flex items-start ${showHeaderIcon ? 'gap-4' : 'gap-0'}`}>
          {showHeaderIcon && (
            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-succession-warning/10">
              <AlertTriangleIcon className="w-6 h-6 text-succession-warning" />
            </div>
          )}
          <div>
            <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>Catastrophic Plan</h3>
            <p className="text-sm text-succession-text-secondary leading-tight" style={{ margin: 0, padding: 0 }}>Emergency & Catastrophe Planning</p>
          </div>
        </div>
        {isApproved && (
          <div className="flex items-center gap-1.5 text-succession-success">
            <CheckCircle2 className="w-5 h-5" />
            <span className="text-sm font-medium">Complete</span>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1">
        {isApproved ? (
          /* Approved — serene success state */
          <div className="flex items-center gap-3 py-2">
            <div className="w-10 h-10 rounded-full bg-succession-success/10 border border-succession-success/20 flex items-center justify-center shrink-0">
              <ShieldIcon className="w-5 h-5 text-succession-success" />
            </div>
            <div>
              <p className="text-sm font-semibold text-succession-success leading-tight">Agreement Approved</p>
              <p className="text-xs text-succession-text-muted mt-0.5 leading-snug">Your plan is on file with your firm.</p>
            </div>
          </div>
        ) : (
          /* Mission status — vertical timeline */
          <div className="relative space-y-2 py-1">
            {/* Rail — sits behind dots, full height */}
            <div className="absolute left-[6px] top-0 bottom-0 w-px bg-succession-border pointer-events-none" />

            {stages.map((stage, i) => {
              const isDone   = stage.done;
              const isActive = !isDone && i === currentIdx;
              const showProgressBar = planBuilderEnabled && isActive && i === 0 && hasPlan && completionPct > 0 && completionPct < 100;

              return (
                <div key={stage.label} className="relative flex items-center gap-2.5">
                  {/* Dot — inline in flex, covers rail with bg */}
                  {isDone ? (
                    <div className="w-3.5 h-3.5 rounded-full bg-succession-success flex items-center justify-center shrink-0 z-10">
                      <CheckCircle2 className="w-2.5 h-2.5 text-white" />
                    </div>
                  ) : isActive ? (
                    <div className="relative w-3.5 h-3.5 shrink-0 z-10 flex items-center justify-center">
                      <div className="absolute w-3.5 h-3.5 rounded-full bg-succession-warning/25 animate-ping" />
                      <div className="w-2.5 h-2.5 rounded-full border-2 border-succession-warning bg-succession-bg-primary" />
                    </div>
                  ) : (
                    <div className="w-3.5 h-3.5 rounded-full border border-succession-border bg-succession-bg-primary shrink-0 z-10" />
                  )}

                  {/* Label */}
                  <div className="flex-1 min-w-0">
                    <span className={`text-xs leading-tight ${
                      isDone    ? 'text-succession-text-muted line-through decoration-succession-border'
                      : isActive ? 'font-semibold text-succession-text-primary'
                      : 'text-succession-text-muted'
                    }`}>
                      {stage.label}
                      {stage.isOptional && (
                        <span className="ml-1 text-xs text-succession-text-muted font-normal">(optional)</span>
                      )}
                    </span>
                    {showProgressBar && (
                      <div className="mt-1 h-0.5 rounded-full bg-succession-border overflow-hidden">
                        <div className="h-full rounded-full bg-succession-warning transition-[width] duration-700 ease-out" style={{ width: `${completionPct}%` }} />
                      </div>
                    )}
                  </div>

                  {/* Badge */}
                  {isDone && (
                    <span className="text-[10px] font-medium text-succession-success bg-succession-success/10 px-1.5 py-0.5 rounded-full shrink-0">Done</span>
                  )}
                  {isActive && (
                    <span className="text-[10px] font-semibold text-succession-warning bg-succession-warning/10 px-1.5 py-0.5 rounded-full shrink-0">Next</span>
                  )}
                </div>
              );
            })}
            {!hasPartner && !isFiled && (() => {
              const partnerStepNum = stages.findIndex(s => s.isOptional) + 1;
              return (
                <p className="mt-1.5 text-[10px] italic text-succession-text-muted pl-6">
                  Step {partnerStepNum} is optional. You can file directly.
                </p>
              );
            })()}
          </div>
        )}
      </div>

      {/* Footer — matches PlanStatusTile exactly */}
      <div className="mt-4 pt-4 border-t border-succession-border">
        {isApproved ? (
          isRj ? (
            <div className="space-y-2">
              {emailSent ? (
                <div className="flex items-center justify-center gap-1.5 text-xs text-succession-success py-2">
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                  Sent — check your inbox.
                </div>
              ) : (
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={() => {
                    requestCatPlanEmail()
                      .then(({ data: d }) => { if (d?.request_cat_plan_email?.success) setEmailSent(true); })
                      .catch(() => setEmailSent(true));
                  }}
                >
                  <ShieldCheckIcon className="w-4 h-4 mr-2" />
                  Email me a copy
                </Button>
              )}
              <div className="flex justify-end">
                <a
                  href="/succession/pact-builder"
                  className="inline-flex items-center text-sm font-medium text-succession-primary hover:text-succession-primary-hover transition-colors group p-0"
                >
                  Update Plan
                  <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          ) : (
            <Button as="a" href="/succession/vault" variant="outline" className="w-full group">
              Download Agreement
              <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          )
        ) : (
          <Button as="a" href="/succession/pact-builder" variant="primary" className="w-full group">
            Continue
            <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Conditional Widgets Grid - shows widgets based on completed activities
const ConditionalWidgetsGrid = ({ isActivityCompleted, multiLineValuationEnabled = false, intl , continuityBuilderEnabled = false, cbWorkflowState = null, cbPartnerStepCompleted = false, isRj = false, planBuilderEnabled = false, searchPartnerOnly = false }) => {
  const showValuation = isActivityCompleted('valuation');
  const showDealRoom = isActivityCompleted('deal_room');
  const showSuccessionPlan = isActivityCompleted('succession_planning') || isActivityCompleted('find_my_gaps');
  const showCatastrophicPlan = isActivityCompleted('catastrophe');
  
  // Build array of guaranteed-visible widgets
  const widgets = [];
  
  if (showValuation) {
    widgets.push({
      id: 'valuation',
      component: multiLineValuationEnabled ? <MultiLineValuationSnapshot showHeaderIcon={false} /> : <ValuationSnapshot showHeaderIcon={false} showApproachTooltips />,
    });
  }
  
  if (showDealRoom) {
    widgets.push({ id: 'deal_room', component: <DealRoomMetricsCard showHeaderIcon={false} /> });
  }
  
  if (showSuccessionPlan) {
    widgets.push({ 
      id: 'succession_plan', 
      component: <SuccessionPlanWidget showHeaderIcon={false} />
    });
  }
  
  if (showCatastrophicPlan) {
    widgets.push({
      id: 'catastrophic_plan',
      component: continuityBuilderEnabled
        ? <CatastrophicPlanCBWidget workflowState={cbWorkflowState} partnerStepCompleted={cbPartnerStepCompleted} showHeaderIcon={false} isRj={isRj} planBuilderEnabled={planBuilderEnabled} searchOnly={searchPartnerOnly} />
        : <CatastrophicPlanWidget showHeaderIcon={false} />,
    });
  }

  // BuyerDealInterestsCard self-hides when there are no active interests (returns null).
  // Include it when the user doesn't own a deal room, but don't let it force the
  // "Your Progress" header to appear on its own since it may render nothing.
  const showBuyerDeals = !showDealRoom;

  // Don't render if no guaranteed widgets and no conditional buyer deals widget
  if (widgets.length === 0 && !showBuyerDeals) return null;

  // When only the buyer deals widget would show (and it may self-hide),
  // wrap it so the header disappears when the component returns null.
  if (widgets.length === 0 && showBuyerDeals) {
    return <BuyerDealsOnlySection intl={intl} />;
  }
  
  return (
    <div className="mt-6 md:mt-8">
      <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>
        {intl.formatMessage({ id: 'succession.launchpad.your_progress' })}
      </h3>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
        {widgets.map((widget) => (
          <div key={widget.id} className="min-h-[200px]">
            {widget.component}
          </div>
        ))}
        {showBuyerDeals && (
          <div className="min-h-[200px]">
            <BuyerDealInterestsCard showHeaderIcon={false} />
          </div>
        )}
      </div>
    </div>
  );
};

// Only renders "Your Progress" + BuyerDealInterestsCard when the user actually
// has active deal interests. Prevents showing an empty header after reset/onboard.
const BuyerDealsOnlySection = ({ intl }) => {
  // Re-use the same query the card uses to decide if there's data
  const { data, loading } = useQuery(gql`
    query BuyerDealsCheck {
      getCurrentUser {
        id
        inventory_user {
          id
          filtered_deal_interests {
            id
          }
        }
      }
    }
  `, { fetchPolicy: 'cache-first', client: apolloClient });

  if (loading) return null;

  const interests = data?.getCurrentUser?.inventory_user?.filtered_deal_interests || [];
  if (interests.length === 0) return null;

  return (
    <div className="mt-6 md:mt-8">
      <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>
        {intl.formatMessage({ id: 'succession.launchpad.your_progress' })}
      </h3>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="min-h-[200px]">
          <BuyerDealInterestsCard showHeaderIcon={false} />
        </div>
      </div>
    </div>
  );
};

const LaunchPad = (props) => {
  const intl = useIntl();
  const userName = (props && (props.current_user?.name || props.user?.name)) || 'there';
  const companyName = (props && (props.company?.branded_name || props.company?.name)) || 'FindBob';
  const companyRawName = (props && props.company?.name) || 'FindBob';
  const dealRoomBrandedName = getDealRoomBrandedName(props.company) || intl.formatMessage({ id: 'succession.messaging.chat_panel.deal_room_label' });
  const features = props.features || (props.company && (props.company.feature_types || props.company.features)) || [];
  const marketplaceTickers = props.marketplace_tickers || [];
  const multiLineValuationEnabled = Array.isArray(features) && features.includes('multi_line_valuation');
  const marketplaceSprintsEnabled = features.includes('marketplace_sprints');
  const aiAssistantEnabled = features.includes('ai_assistant');
  const georgeCoachEnabled = features.includes('george_hartman_coach_agent');
  const valueAccelerationEnabled = features.includes('value_acceleration');
  const guidedTourEnabled = features.includes('guided_tour');
  const userId = props.current_user?.id || props.user?.id;
  const logoDark = props.logo_dark; // White/dark version of company logo
  const companyCalendlyLink = props.company?.calendly_link || props.company?.calendlyLink || null;
  const consultationUrl = companyCalendlyLink || 'https://calendly.com/findbob/consultation';
  // When this flag is on, the "Book a Consultation" affordances (Quickstart card,
  // welcome banner link, NeedHelpPanel rows) are all suppressed across LaunchPad.
  const consultationDisabled = Array.isArray(features) && features.includes('disabled_book_a_consultation');
  // When this flag is on, sidebar category links don't navigate to landing pages, so the
  // matching category CTAs at the bottom of LaunchPad (succession/growth/value_acceleration)
  // are hidden for consistency. The Education resource card stays — it's not a category.
  const categoryLandingPagesDisabled = Array.isArray(features) && features.includes('disable_category_landing_pages');

  // Welcome video — feature-gated AND requires the company to have a URL configured.
  const welcomeVideoFlag = Array.isArray(features) && features.some((f) => String(f) === 'launchpad_show_welcome_video' || String(f) === 'launchpad_show_welcome_video_type');
  const welcomeVideoUrl = props.company?.welcome_video_url || null;
  const welcomeVideoEnabled = welcomeVideoFlag && !!welcomeVideoUrl;
  const videoWatchedKey = userId ? `${VIDEO_WATCHED_KEY_PREFIX}${userId}` : null;
  const [videoWatched, setVideoWatched] = useState(() => {
    if (!videoWatchedKey) return false;
    try { return localStorage.getItem(videoWatchedKey) === '1'; } catch (_) { return false; }
  });
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const handleVideoWatched = () => {
    if (videoWatched) return;
    setVideoWatched(true);
    if (videoWatchedKey) {
      try { localStorage.setItem(videoWatchedKey, '1'); } catch (_) { /* noop */ }
    }
  };
  // Mirror the welcome banner's own dismiss key here so we can decide whether the
  // banner is currently serving as the video entry point. When it's dismissed, the
  // QuickstartBlock injection becomes the fallback entry point even in empty state.
  const welcomeBannerDismissKey = `succession_quickstart_welcome_dismissed_${userId || 'anon'}`;
  const [welcomeBannerDismissed, setWelcomeBannerDismissed] = useState(() => {
    try { return localStorage.getItem(welcomeBannerDismissKey) === 'true'; } catch (_) { return false; }
  });
  
  const [tourOpen, setTourOpen] = useState(false);
  // Controls local visibility of the assessment prompt (optimistic hide on skip/complete)
  const [assessmentPromptDismissed, setAssessmentPromptDismissed] = useState(false);
  // Retake of an already-completed assessment, opened from the score card.
  const [retakingAssessment, setRetakingAssessment] = useState(false);
  const [retakingFlowOpen, setRetakingFlowOpen] = useState(false);
  // Category whose review toggle is in flight, so only that row's control disables.
  const [reviewingCategory, setReviewingCategory] = useState(null);

  // Fetch plan status data for activity completion tracking
  const { data: planStatusData, refetch: refetchPlanStatus } = useQuery(GET_SUCCESSION_PLAN_STATUS, { fetchPolicy: 'cache-and-network', client: apolloClient });
  const [restartAssessment] = useMutation(RESTART_READINESS_ASSESSMENT, { client: apolloClient });
  const [toggleAreaReview] = useMutation(TOGGLE_READINESS_AREA_REVIEW, { client: apolloClient });

  // Readiness assessment data from GraphQL
  const assessmentResult = planStatusData?.readinessAssessmentResult || null;
  const assessmentPlanSlug = planStatusData?.successionReadinessPlanSlug || 'succession-readiness-assessment';

  // Whether to show the prompt is computed server-side (feature on + template
  // seeded + not completed/skipped + not a legacy Paddy completer) so it never
  // flashes before data loads. `assessmentPromptDismissed` hides it optimistically
  // within the session after the user completes or skips.
  const showAssessmentPrompt = planStatusData?.showReadinessAssessmentPrompt === true &&
    !assessmentPromptDismissed;

  const handleAssessmentComplete = (result) => {
    setAssessmentPromptDismissed(true);
    refetchPlanStatus();
  };

  const handleAssessmentSkip = () => {
    setAssessmentPromptDismissed(true);
  };

  // Retake: reopen the completed assessment and show the flow. The previous
  // result stays on screen behind the modal, so abandoning the retake costs the
  // advisor nothing visible.
  const handleRetakeAssessment = async () => {
    if (retakingAssessment) return;
    setRetakingAssessment(true);
    try {
      if (window.ahoy) window.ahoy.track('readiness_assessment_retake_started');
    } catch (_) { /* ahoy optional */ }
    try {
      const { data } = await restartAssessment();
      const errors = data?.restartReadinessAssessment?.errors || [];
      if (errors.length > 0) {
        console.error('restartReadinessAssessment errors:', errors);
        return;
      }
      setRetakingFlowOpen(true);
    } catch (err) {
      console.error('Failed to reopen readiness assessment:', err);
    } finally {
      setRetakingAssessment(false);
    }
  };

  // Mark a readiness area reviewed, or clear the mark. Only meaningful for the
  // four categories with no server-side signal (partnering, deal_structure,
  // communication, emotional_readiness) — the card hides the control elsewhere.
  const handleToggleAreaReview = async (category, reviewed) => {
    if (reviewingCategory) return;
    setReviewingCategory(category);
    try {
      const { data } = await toggleAreaReview({ variables: { category, reviewed } });
      const errors = data?.toggleReadinessAreaReview?.errors || [];
      if (errors.length > 0) {
        console.error('toggleReadinessAreaReview errors:', errors);
      }
    } catch (err) {
      console.error('Failed to toggle readiness area review:', err);
    } finally {
      setReviewingCategory(null);
    }
  };

  const handleRetakeComplete = () => {
    setRetakingFlowOpen(false);
    refetchPlanStatus();
  };

  // Motivation can change at runtime (via ModernizedMotivationOnboarding modal).
  // Prefer the live Apollo query value so LaunchPad re-renders into the right persona
  // theme without a page reload; fall back to the server-rendered prop on first paint.
  const userMotivation = planStatusData?.getCurrentUser?.motivation || props.current_user?.motivation || props.user?.motivation || null;
  const heatmapEnabled = features.includes('heatmap') && userMotivation === 'growth';

  // Theme for banner styling
  const { company: themeCompany, isDarkTheme } = useSuccessionTheme();
  const primaryColor = themeCompany?.primary_color || '#5489B6';

  // Legacy catastrophic plan banner dismissal
  const legacyBannerKey = userId ? `legacy_cat_plan_banner_dismissed_${userId}` : null;
  const [legacyBannerDismissed, setLegacyBannerDismissed] = useState(() => {
    if (!legacyBannerKey) return false;
    try { return !!localStorage.getItem(legacyBannerKey); } catch (_) { return false; }
  });
  const handleDismissLegacyBanner = () => {
    setLegacyBannerDismissed(true);
    if (legacyBannerKey) {
      try { localStorage.setItem(legacyBannerKey, '1'); } catch (_) {}
    }
  };

  // Merge GraphQL plan data with props.current_user for activity completion checks.
  // Overlay live motivation/approval_state/pending_motivation from the query so that
  // a runtime motivation change (via ModernizedMotivationOnboarding modal) propagates
  // through useGettingStartedData and the Quickstart Guide without a page reload.
  //
  // We also null out `suggested_activities` when the live motivation differs from the
  // server-rendered prop. Otherwise the hook's `provided` branch short-circuits with the
  // stale Rails-rendered activity list and ignores the new motivation entirely.
  const currentUserWithPlans = useMemo(() => {
    const mySuccessionPlans = planStatusData?.mySuccessionPlans || [];
    const liveUser = planStatusData?.getCurrentUser || {};
    const motivationChanged =
      liveUser.motivation &&
      liveUser.motivation !== (props.current_user?.motivation || props.user?.motivation);
    return {
      ...props.current_user,
      ...(liveUser.motivation ? { motivation: liveUser.motivation } : {}),
      ...(liveUser.approval_state ? { approval_state: liveUser.approval_state } : {}),
      ...(liveUser.pending_motivation ? { pending_motivation: liveUser.pending_motivation } : {}),
      // RJ Seller Visibility — these are Booleans, so they must be overlaid on
      // a null check, not truthiness. A live `false` (seller just opted out) is
      // meaningful and has to win over the stale server-rendered prop; the
      // `? :` form used above would silently discard it.
      ...(liveUser.marketplace_visibility_opt_in != null
        ? { marketplace_visibility_opt_in: liveUser.marketplace_visibility_opt_in }
        : {}),
      ...(liveUser.minimum_profile_completed != null
        ? { minimum_profile_completed: liveUser.minimum_profile_completed }
        : {}),
      ...(liveUser.modernized_onboarding_completed != null
        ? { modernized_onboarding_completed: liveUser.modernized_onboarding_completed }
        : {}),
      ...(motivationChanged ? { suggested_activities: null } : {}),
      mySuccessionPlans,
    };
  }, [props.current_user, props.user, planStatusData]);

  // Get getting started data for progress tracking and conditional widgets.
  // Pass the completed assessment result so card ordering can be personalised.
  const { activities, isActivityCompleted, percent, completed, total, hasAlwaysVisibleActivity } = useGettingStartedData(
    currentUserWithPlans,
    props.company,
    features,
    { source: 'launchpad', assessmentResult: assessmentResult && assessmentResult.completed ? assessmentResult : null },
    intl
  );

  // Inject the video task into the quickstart list when:
  //  - feature is enabled (flag + URL set)
  //  - user hasn't watched yet
  //  - the welcome banner ISN'T currently the entry point (either we're past empty
  //    state, OR the user has dismissed the banner). Otherwise the inline player
  //    in the welcome banner already covers it and we'd be duplicating the affordance.
  const isEmptyState = completed === 0;
  // Existing users arriving at the modernized LaunchPad for the first time carry
  // quickstart progress over from the legacy app, so they're not at 0% and would
  // otherwise never see the welcome card (and its video). When the
  // launchpad_welcome_backfill flag is on, also show it on their first modern
  // visit (server-tracked via users.launchpad_first_accessed_date).
  const welcomeBackfillEnabled = Array.isArray(features) && features.includes('launchpad_welcome_backfill');
  const firstLaunchpadVisit = !!(props.current_user?.first_launchpad_visit);
  const showWelcomeBanner = isEmptyState || (welcomeBackfillEnabled && firstLaunchpadVisit);
  const welcomeBannerActiveAsEntryPoint = showWelcomeBanner && !welcomeBannerDismissed;
  const injectVideoTask = welcomeVideoEnabled && !videoWatched && !welcomeBannerActiveAsEntryPoint;
  const videoActivity = {
    id: VIDEO_TASK_ID,
    title: intl.formatMessage({ id: 'succession.launchpad.welcome_video_task_title' }),
    description: intl.formatMessage({ id: 'succession.launchpad.welcome_video_task_description' }),
    // Render hint consumed by QuickstartBlock — opens the dialog instead of routing.
    videoTask: true,
  };
  // RJ Seller Visibility — local mirror of the opt-in state so an inline toggle
  // reflects immediately. Seeded from the live query (which the overlay above
  // already merged into currentUserWithPlans) and re-synced when that changes,
  // so a toggle made in Settings in another tab is not stale here.
  const liveOptedIn = currentUserWithPlans?.marketplace_visibility_opt_in === true;
  const [visibilitySheetOpen, setVisibilitySheetOpen] = useState(false);
  const [visibilityOptedIn, setVisibilityOptedIn] = useState(liveOptedIn);
  useEffect(() => { setVisibilityOptedIn(liveOptedIn); }, [liveOptedIn]);

  const hasVisibilityCard = activities.some((a) => a.id === MARKETPLACE_VISIBILITY_ID);

  // Impression: fired once per mount when the card is actually rendered.
  useEffect(() => {
    if (!hasVisibilityCard) return;
    if (typeof window !== 'undefined' && window.ahoy) {
      try {
        window.ahoy.track('rj_visibility_card_impression', { opted_in: visibilityOptedIn });
      } catch (e) { /* tracking must never break render */ }
    }
    // Deliberately keyed on card presence only. `visibilityOptedIn` is read but
    // intentionally not a dependency: re-firing on every opt-in change would
    // inflate impressions with what are really toggle events.
  }, [hasVisibilityCard]);

  // Attach the activation hint so the card opens the sheet instead of routing
  // to Settings. Title/description already reflect state from the hook.
  const activitiesWithHandlers = useMemo(() => activities.map((a) => (
    a.id === MARKETPLACE_VISIBILITY_ID
      ? { ...a, onActivate: () => setVisibilitySheetOpen(true) }
      : a
  )), [activities]);

  const activitiesWithVideo = injectVideoTask
    ? [videoActivity, ...activitiesWithHandlers]
    : activitiesWithHandlers;
  const isActivityCompletedWithVideo = (id) => (
    id === VIDEO_TASK_ID ? videoWatched : isActivityCompleted(id)
  );
  
  // Build context-aware help items for the NeedHelpPanel inside QuickstartBlock
  const helpConfig = useMemo(() => {
    const consultationPath = consultationUrl;
    const valuationPath = multiLineValuationEnabled ? '/succession/valuation/multi' : '/succession/valuation';

    if (completed === 0) {
      const items = [
        { text: intl.formatMessage({ id: 'succession.launchpad.help_run_valuation' }), detail: intl.formatMessage({ id: 'succession.launchpad.help_run_valuation_detail' }), href: valuationPath },
        { text: intl.formatMessage({ id: 'succession.launchpad.help_have_numbers' }), detail: intl.formatMessage({ id: 'succession.launchpad.help_have_numbers_detail' }), href: '/succession/plans/comprehensive-succession-plan-v2' },
      ];
      if (!consultationDisabled) {
        items.push({ text: intl.formatMessage({ id: 'succession.launchpad.help_want_to_talk' }), detail: intl.formatMessage({ id: 'succession.launchpad.help_want_to_talk_detail' }), href: consultationPath });
      }
      return {
        heading: intl.formatMessage({ id: 'succession.launchpad.not_sure_where_to_start' }),
        subheading: intl.formatMessage({ id: 'succession.launchpad.here_is_what_we_recommend' }),
        items,
      };
    }

    const items = [
      {
        text: intl.formatMessage({ id: 'succession.launchpad.help_complete_tasks' }),
        detail: intl.formatMessage({ id: 'succession.launchpad.help_complete_tasks_detail' }),
        href: '#quickstart-guide',
        onClick: () => { document.getElementById('quickstart-guide')?.scrollIntoView({ behavior: 'smooth' }); },
      },
      { text: intl.formatMessage({ id: 'succession.launchpad.help_skip_ahead' }), detail: intl.formatMessage({ id: 'succession.launchpad.help_skip_ahead_detail' }), href: '/succession/succession' },
    ];
    if (!consultationDisabled) {
      items.push({ text: intl.formatMessage({ id: 'succession.launchpad.help_need_guidance' }), detail: intl.formatMessage({ id: 'succession.launchpad.help_need_guidance_detail' }), href: consultationPath });
    }
    return {
      heading: intl.formatMessage({ id: 'succession.launchpad.not_sure_what_next' }),
      subheading: intl.formatMessage({ id: 'succession.launchpad.here_is_how_to_move_forward' }),
      items,
    };
  }, [completed, multiLineValuationEnabled, consultationUrl, consultationDisabled]);

  // Auto-open tour on first visit (per-user localStorage gate)
  // Don't auto-start if the motivation modal is shown - wait until user completes it
  useEffect(() => {
    if (!userId) return;
    if (!guidedTourEnabled) return;
    // If motivation modal is shown, don't auto-start the tour - it will start after user
    // completes the modal and the page reloads
    if (props.show_motivation_modal) return;
    
    const storageKey = `${TOUR_STORAGE_KEY_PREFIX}${userId}`;
    try {
      const params = new URLSearchParams(window.location.search);
      // Allow ?tour=open to force open, ?tour=reset to reset and show
      if (params.get('tour') === 'reset') {
        localStorage.removeItem(storageKey);
        setTourOpen(true);
        return;
      }
      if (params.get('tour') === 'open') {
        setTourOpen(true);
        return;
      }
      const alreadyShown = localStorage.getItem(storageKey);
      if (!alreadyShown) {
        // Short delay to ensure page renders first
        const timer = setTimeout(() => setTourOpen(true), 500);
        return () => clearTimeout(timer);
      }
    } catch (_) {}
  }, [userId, props.show_motivation_modal, guidedTourEnabled]);

  const handleTourClose = () => {
    setTourOpen(false);
    if (userId) {
      try { localStorage.setItem(`${TOUR_STORAGE_KEY_PREFIX}${userId}`, '1'); } catch (_) {}
    }
  };

  const handleTourComplete = () => {
    handleTourClose();
  };

  // Build tour steps dynamically based on features
  const tourSteps = useMemo(() => {
    const steps = [];
    
    // Step 1: AI Assistant (if enabled)
    if (aiAssistantEnabled) {
      steps.push({
        id: 'ai-assistant',
        title: intl.formatMessage({ id: 'succession.launchpad.tour_ai_title' }, { companyName }),
        description: intl.formatMessage({ id: 'succession.launchpad.tour_ai_description' }, { companyName }),
        targetSelector: '[data-tutorial-id="launchpad-assistant"]',
        position: 'right',
      });
    }

    // Step 2: Quickstart Guide
    steps.push({
      id: 'quickstart-guide',
      title: intl.formatMessage({ id: 'succession.launchpad.tour_quickstart_title' }),
      description: intl.formatMessage({ id: 'succession.launchpad.tour_quickstart_description' }),
      targetSelector: '[data-tutorial-id="launchpad-quickstart"]',
      position: 'right',
    });
    
    return steps;
  }, [aiAssistantEnabled, companyName]);

  const { data: featuredProgramsData, loading: featuredProgramsLoading } = useQuery(FEATURED_PROGRAMS_QUERY, {
    skip: !marketplaceSprintsEnabled,
    client: apolloClient,
  });
  
  const featuredPrograms = featuredProgramsData?.sprintPrograms || [];

  // Dynamic subtitle based on motivation and whether this is a first visit (same condition as welcome banner)
  const pageSubtitle = useMemo(() => {
    const isFirstVisit = completed === 0;
    if (isFirstVisit) {
      if (userMotivation === 'succession') return intl.formatMessage({ id: 'succession.launchpad.subtitle_succession_first' });
      if (userMotivation === 'growth') return intl.formatMessage({ id: 'succession.launchpad.subtitle_growth_first' });
      return intl.formatMessage({ id: 'succession.launchpad.subtitle_first_visit' });
    }
    if (userMotivation === 'succession') return intl.formatMessage({ id: 'succession.launchpad.subtitle_succession_progress' });
    if (userMotivation === 'growth') return intl.formatMessage({ id: 'succession.launchpad.subtitle_growth_progress' });
    return intl.formatMessage({ id: 'succession.launchpad.subtitle_progress' });
  }, [completed, userMotivation, intl]);

  return (
    <SingleColumnTemplate
      title={intl.formatMessage({ id: 'succession.launchpad.welcome_title' }, { userName })}
      subtitle={pageSubtitle}
      columnClassName="max-w-2xl"
      columnStyle={{ maxWidth: '55rem' }}
    >
      {/* Marketplace Ticker — full-width strip directly beneath header, feature-gated */}
      {marketplaceTickers.length > 0 && (
        <div style={{ margin: '0 -1rem' }}>
          <SuccessionMarketplaceTicker tickers={marketplaceTickers} locale={props.locale || 'en'} />
        </div>
      )}

      {/* Marketplace Approval Status Banner — shown when tenant has approval process enabled.
          Prefer the live Apollo query value so submitting the modal updates this banner without
          a page reload; fall back to the server-rendered prop on first paint. */}
      <ApprovalStatusBanner
        approvalState={planStatusData?.getCurrentUser?.approval_state || props.current_user?.approval_state}
        motivation={planStatusData?.getCurrentUser?.motivation || userMotivation}
        pendingMotivation={planStatusData?.getCurrentUser?.pending_motivation || props.current_user?.pending_motivation}
        features={features}
        userId={props.current_user?.id}
        reviewerLabel={props.company?.approval_reviewer_label}
      />

      {/* Welcome Message - Shown at 0% quickstart completion (post-onboarding /
          post-motivation modal), and to existing users on their first modernized
          LaunchPad visit when launchpad_welcome_backfill is enabled. */}
      {showWelcomeBanner && (
        <QuickstartWelcomeMessage
          userName={userName}
          companyName={companyName}
          companyRawName={companyRawName}
          dealRoomBrandedName={dealRoomBrandedName}
          motivation={userMotivation}
          consultationUrl={consultationDisabled ? null : companyCalendlyLink}
          userId={userId}
          simpleMarketplaceApprovalEnabled={Array.isArray(features) && features.includes('simple_marketplace_approval')}
          valuationEnabled={multiLineValuationEnabled || (Array.isArray(features) && features.includes('custom_valuation'))}
          showVideo={welcomeVideoEnabled}
          videoWatched={videoWatched}
          videoUrl={welcomeVideoUrl}
          onVideoWatched={handleVideoWatched}
          onDismiss={() => setWelcomeBannerDismissed(true)}
        />
      )}

      {/* Getting Started Progress + Profile Completion - Only shown when user has made progress but not yet complete (progressive disclosure) */}
      {completed > 0 && completed < total && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-stretch">
          <GettingStartedProgress completedCount={completed} totalCount={total} className="md:col-span-3 h-full" />
          <ProfileCompletionCard elevateOnHover={false} compact className="md:col-span-2 h-full" />
        </div>
      )}

      {/* Seller Heatmap — gated by heatmap feature flag */}
      {heatmapEnabled && (
        <div className="mt-6 md:mt-8">
          <SellerHeatMapLaunchpadContainer heatmapEnabled={heatmapEnabled} />
        </div>
      )}

      {/* AI Assistant */}
      {aiAssistantEnabled && (
        <div data-tutorial-id="launchpad-assistant" className="mt-6 md:mt-8">
          <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>
            {georgeCoachEnabled
              ? intl.formatMessage({ id: 'succession.launchpad.ask_george' })
              : intl.formatMessage({ id: 'succession.launchpad.ask_company_ai' }, { companyName })}
          </h3>
          <div className="mt-2">
            <AssistantQuickAsk companyBrandedName={companyName} georgeCoachEnabled={georgeCoachEnabled} />
          </div>
          <div className="mt-2 text-center text-xs text-succession-text-secondary">
            {georgeCoachEnabled
              ? intl.formatMessage({ id: 'succession.launchpad.george_ai_disclaimer' })
              : intl.formatMessage({ id: 'succession.launchpad.ai_disclaimer' }, { companyName })}
          </div>
        </div>
      )}

      {/* Conditional Widgets - Progressive disclosure based on completed activities */}
      <ConditionalWidgetsGrid
        isActivityCompleted={isActivityCompleted}
        multiLineValuationEnabled={multiLineValuationEnabled}
        continuityBuilderEnabled={Array.isArray(features) && features.includes('continuity_builder')}
        planBuilderEnabled={Array.isArray(features) && features.some((f) => String(f) === 'plan_builder' || String(f) === 'plan_builder_type')}
        searchPartnerOnly={!(Array.isArray(features) && features.some((f) => String(f) === 'show_file_your_partner_button' || String(f) === 'show_file_your_partner_button_type'))}
        cbWorkflowState={currentUserWithPlans?.continuity_builder_workflow_state || null}
        cbPartnerStepCompleted={currentUserWithPlans?.continuity_builder_partner_step_completed === true}
        isRj={props.is_rj === true}
        intl={intl}
      />

      {/* Legacy catastrophic plan migration banner */}
      {(() => {
        const legacyData = planStatusData?.getCurrentUser;
        const hasLegacyPlan = legacyData?.has_legacy_catastrophic_plan;
        const hasCatPlanInProgress = (planStatusData?.mySuccessionPlans || []).some(p => p.planTemplate?.planType === 'catastrophic');
        const showBanner = hasLegacyPlan && !hasCatPlanInProgress && !legacyBannerDismissed && !isActivityCompleted('catastrophe');
        if (!showBanner) return null;

        const bgColor = isDarkTheme ? 'rgba(255,255,255,0.06)' : 'rgba(84,137,182,0.08)';
        const borderColor = primaryColor;

        return (
          <div
            className="mt-4 flex items-center justify-between gap-3 px-4 py-3 rounded-lg"
            style={{
              backgroundColor: bgColor,
              border: `1px solid ${primaryColor}`,
              transition: 'background-color 0.3s ease, border-color 0.3s ease',
            }}
          >
            <div className="flex items-center gap-3 min-w-0">
              <ShieldCheckIcon
                className="h-5 w-5 shrink-0"
                style={{ color: primaryColor, transition: 'color 0.3s ease' }}
              />
              <p className="text-sm font-medium text-succession-text-primary transition-colors duration-300">
                You have an existing catastrophic plan on file.{' '}
                <a
                  href="/succession/pact-builder"
                  className="font-semibold underline hover:no-underline"
                  style={{ color: primaryColor, transition: 'color 0.3s ease' }}
                >
                  File or Review Agreement →
                </a>
              </p>
            </div>
            <button
              onClick={handleDismissLegacyBanner}
              className="shrink-0 p-1 rounded transition-colors duration-300 hover:bg-black/10"
              aria-label="Dismiss"
              style={{ color: primaryColor }}
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>
        );
      })()}

      {/* Readiness assessment sits directly above the QuickStart guide, because
          the two are one story: the assessment produces the gaps, and the guide
          is the answer to them. Higher up the page it read as an unrelated
          widget, and the "Your Recommended Next Steps" heading below it had
          nothing visible to refer back to.

          Prompt when not yet taken; score card once completed. The prompt also
          hides the guide (see below), so the two never share the slot. */}
      {showAssessmentPrompt && (
        <div className="mt-6 md:mt-8">
          <ReadinessAssessmentPrompt
            planSlug={assessmentPlanSlug}
            onComplete={handleAssessmentComplete}
            onSkip={handleAssessmentSkip}
          />
        </div>
      )}

      {!showAssessmentPrompt && assessmentResult && assessmentResult.completed && (
        <div className="mt-6 md:mt-8">
          <ReadinessScoreCard
            result={assessmentResult}
            onRetake={handleRetakeAssessment}
            retaking={retakingAssessment}
            onToggleReview={handleToggleAreaReview}
            reviewingCategory={reviewingCategory}
            downloadUrl="/succession/readiness/export.pdf"
          />
        </div>
      )}

      {/* Retake — a portalled modal, so this renders over the page wherever it
          sits in the tree. Kept next to the card that triggers it. */}
      {retakingFlowOpen && (
        <ReadinessAssessmentFlow
          planSlug={assessmentPlanSlug}
          onComplete={handleRetakeComplete}
          onClose={() => setRetakingFlowOpen(false)}
        />
      )}

      {/* Quickstart Guide - Hidden when all activities are complete, and also
          hidden while the readiness assessment prompt is showing (the prompt
          takes this slot until the assessment is completed or skipped).
          currentUserWithPlans already overlays live motivation/approval/pending values
          from Apollo on top of the stale server-rendered props. */}
      {/* `hasAlwaysVisibleActivity` keeps the block mounted past the normal
          collapse point when it contains a state-reflective card (the RJ
          marketplace visibility tile). Without it, finishing every real
          activity would hide the one control the seller needs to keep
          reaching — the exact auto-hide the ticket rules out. For everyone
          else the condition is unchanged. */}
      {!showAssessmentPrompt && (completed < total || hasAlwaysVisibleActivity) && (
        <QuickstartBlock
          activities={activitiesWithVideo}
          isActivityCompleted={isActivityCompletedWithVideo}
          completedCount={completed}
          currentUser={currentUserWithPlans || props.current_user || props.user}
          features={features}
          onOpenVideo={() => setVideoDialogOpen(true)}
          showRewatchVideo={welcomeVideoEnabled && videoWatched}
          // assessmentResult is null unless succession_readiness is on (the
          // resolver gates it), so a completed result is sufficient here.
          personalizedByAssessment={Boolean(assessmentResult?.completed)}
        />
      )}

      {/* Education and resources */}
      <div className="mt-6 md:mt-8">
        <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>{intl.formatMessage({ id: 'succession.launchpad.education_and_resources' })}</h3>

        {/* Row 1: single large card */}
        <div className="mt-2">
          <Card variant="brandSecondary" className="overflow-hidden relative group">
            {/* Background: Company logo (white version) or fallback to GraduationCap icon */}
            <div className="absolute right-4 bottom-4 opacity-20 group-hover:opacity-30 transition-opacity pointer-events-none">
              {logoDark ? (
                <img
                  src={logoDark}
                  alt=""
                  className="h-16 w-auto object-contain"
                />
              ) : (
                <GraduationCap className="w-32 h-32" strokeWidth={1.5} />
              )}
            </div>
            <div className="p-4 md:p-6 flex items-center justify-between gap-4 relative z-10">
              <div>
                <div className="text-sm opacity-85">{intl.formatMessage({ id: 'succession.launchpad.education_label' })}</div>
                <div className="text-xl md:text-2xl font-semibold mt-1">{intl.formatMessage({ id: 'succession.launchpad.browse_education' })}</div>
              </div>
              <div className="shrink-0">
                <Button
                  as="a"
                  href="/succession/education"
                  className="bg-succession-bg-primary text-succession-text-primary border border-succession-border hover:bg-succession-bg-tertiary"
                >
                  {intl.formatMessage({ id: 'succession.launchpad.learn_more' })}
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Row 2: two or three small category CTAs — hidden when category landing pages are disabled. */}
        {!categoryLandingPagesDisabled && (
          <div className={`mt-3 grid grid-cols-1 ${valueAccelerationEnabled ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-3`}>
            <a href="/succession/succession" className="block">
              <Card variant="brandPrimary" className="h-full overflow-hidden relative group">
                {/* Background Icon */}
                <div className="absolute -right-6 -bottom-6 opacity-10 group-hover:opacity-15 transition-opacity">
                  <Anchor className="w-32 h-32" strokeWidth={1.5} />
                </div>
                <div className="p-4 md:p-6 relative z-10">
                  <div className="text-sm opacity-85">{intl.formatMessage({ id: 'succession.launchpad.succession_label' })}</div>
                  <div className="mt-1 font-semibold">{intl.formatMessage({ id: 'succession.launchpad.continue_succession_journey' })}</div>
                </div>
              </Card>
            </a>
            <a href="/succession/growth" className="block">
              <Card variant="brandSecondary" className="h-full overflow-hidden relative group">
                {/* Background Icon */}
                <div className="absolute -right-6 -bottom-6 opacity-10 group-hover:opacity-15 transition-opacity">
                  <TrendingUp className="w-32 h-32" strokeWidth={1.5} />
                </div>
                <div className="p-4 md:p-6 relative z-10">
                  <div className="text-sm opacity-85">{intl.formatMessage({ id: 'succession.launchpad.growth_label' })}</div>
                  <div className="mt-1 font-semibold">{intl.formatMessage({ id: 'succession.launchpad.explore_growth' })}</div>
                </div>
              </Card>
            </a>
            {valueAccelerationEnabled && (
              <a href="/succession/value_acceleration" className="block">
                <Card variant="brandAccent" className="h-full overflow-hidden relative group">
                  {/* Background Icon */}
                  <div className="absolute -right-6 -bottom-6 opacity-10 group-hover:opacity-15 transition-opacity">
                    <Zap className="w-32 h-32" strokeWidth={1.5} />
                  </div>
                  <div className="p-4 md:p-6 relative z-10">
                    <div className="text-sm opacity-85">{intl.formatMessage({ id: 'succession.launchpad.value_building_label' })}</div>
                    <div className="mt-1 font-semibold">{intl.formatMessage({ id: 'succession.launchpad.learn_value_acceleration' })}</div>
                  </div>
                </Card>
              </a>
            )}
          </div>
        )}
      </div>

      {/* Featured Coach-Led Programs */}
      {marketplaceSprintsEnabled && (
        <div className="mt-6 md:mt-8">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>{intl.formatMessage({ id: 'succession.launchpad.featured_programs' })}</h3>
            <Button
              as="a"
              href="/sprints/marketplace"
              variant="ghost"
              size="sm"
              className="text-succession-text-secondary hover:text-succession-primary"
            >
              {intl.formatMessage({ id: 'succession.launchpad.see_all_programs' })}
            </Button>
          </div>

          {featuredProgramsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-2xl border border-succession-border bg-succession-bg-primary p-6 animate-pulse">
                  <div className="h-48 bg-succession-bg-secondary rounded-lg mb-4"></div>
                  <div className="h-4 bg-succession-bg-secondary rounded mb-2"></div>
                  <div className="h-3 bg-succession-bg-secondary rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : featuredPrograms.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {featuredPrograms.map((program) => (
                <ProgramCard
                  key={program.id}
                  program={program}
                  coach={program.coachProfile}
                />
              ))}
            </div>
          ) : (
            <Card variant="brandSecondary" className="p-6">
              <div className="flex items-center gap-3">
                <Rocket className="w-6 h-6 opacity-60" />
                <div>
                  <div className="font-semibold text-succession-text-primary">{intl.formatMessage({ id: 'succession.launchpad.no_programs_title' })}</div>
                  <div className="text-sm text-succession-text-secondary mt-1">
                    {intl.formatMessage({ id: 'succession.launchpad.no_programs_subtitle' })}
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Welcome Video Dialog — mounted globally so it can be opened from the empty-state
          thumbnail, the injected quickstart task, or the Rewatch link in QuickstartBlock. */}
      {welcomeVideoEnabled && (
        <WelcomeVideoDialog
          open={videoDialogOpen}
          onOpenChange={setVideoDialogOpen}
          onWatched={handleVideoWatched}
          videoSrc={welcomeVideoUrl}
          title={intl.formatMessage({ id: 'succession.launchpad.welcome_video_dialog_title' })}
        />
      )}

      {/* RJ Seller Visibility — inline toggle sheet opened from the quickstart
          card. Refetches plan status on success so the card's title and check
          state come from the same source of truth as everything else. */}
      <RjVisibilitySheet
        open={visibilitySheetOpen}
        optedIn={visibilityOptedIn}
        minimumProfileCompleted={currentUserWithPlans?.minimum_profile_completed === true}
        onClose={() => setVisibilitySheetOpen(false)}
        onToggled={(next) => {
          setVisibilityOptedIn(next);
          refetchPlanStatus();
        }}
      />

      {/* RJ Seller Visibility — first-run explainer. This mount is the point of
          the ticket: previously it existed only on /succession/settings, the
          very page a seller had to already know about.

          The gate suppresses rather than queues. ModernizedMotivationOnboarding
          renders at zIndex 99999999999 over an opaque backdrop while this modal
          is z-50, so if both mounted this one would open invisibly underneath
          and then snap into view when onboarding unmounted. Requiring
          modernized_onboarding_completed — the same signal the onboarding
          container uses to decide it is finished — means the two can never
          contradict each other, and this simply picks up on the next visit.
          approval_state must not be pending: a seller awaiting manager approval
          stays fully dark. */}
      <RjVisibilityFirstRunModal
        userId={currentUserWithPlans?.id}
        dismissedAt={currentUserWithPlans?.rj_visibility_first_run_dismissed_at}
        enabled={
          Array.isArray(features) &&
          features.includes('rj_seller_visibility') &&
          currentUserWithPlans?.motivation === 'succession' &&
          !(currentUserWithPlans?.approval_state === 'pending' || currentUserWithPlans?.approval_state === 0) &&
          currentUserWithPlans?.modernized_onboarding_completed === true
        }
      />

      {/* Guided Tour — gated by guided_tour feature */}
      {guidedTourEnabled && (
        <GuidedTour
          isOpen={tourOpen}
          steps={tourSteps}
          onClose={handleTourClose}
          onComplete={handleTourComplete}
          onReplayRequest={() => setTourOpen(true)}
        />
      )}
    </SingleColumnTemplate>
  );
};

// Quickstart block - receives activities, completion checker, and help config from parent
const QuickstartBlock = ({ activities = [], isActivityCompleted, completedCount = 0, currentUser = {}, features = [], helpHeading, helpSubheading, helpItems, onOpenVideo, showRewatchVideo = false, personalizedByAssessment = false }) => {
  const intl = useIntl();
  // Determine which activity gets the "Start Here" or "Next Step" badge
  // Always skip "consultation" — it should never receive the badge
  // `marketplace_visibility` is skipped alongside `consultation`: it is
  // reversible state, not a step, and badging it "Start Here" would nag a
  // seller who has deliberately opted out. Stay visible but neutral.
  const firstIncompleteId = useMemo(() => {
    const found = activities.find(
      (a) => a.id !== 'consultation' && a.id !== MARKETPLACE_VISIBILITY_ID && !isActivityCompleted(a.id)
    );
    return found ? found.id : null;
  }, [activities, isActivityCompleted]);

  const badgeLabelForId = (id) => {
    if (id !== firstIncompleteId) return null;
    return completedCount === 0 ? intl.formatMessage({ id: 'succession.launchpad.start_here' }) : intl.formatMessage({ id: 'succession.launchpad.next_step' });
  };

  const tallVariantEnabled = Array.isArray(features) && features.includes('getting_started_mini_card_tall');

  return (
    <div id="quickstart-guide" className="mt-6 md:mt-8" data-tutorial-id="launchpad-quickstart">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* Once a completed readiness assessment has reordered these cards they
            are no longer a generic quickstart — they are the answer to the
            assessment, so the heading says so. */}
        <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>{intl.formatMessage({ id: personalizedByAssessment ? 'succession.launchpad.recommended_next_steps_title' : 'succession.launchpad.quickstart_guide_title' })}</h3>
        {/* Rewatch link — replaces the polymet FAB. Visible once the video has been watched. */}
        {showRewatchVideo && (
          <button
            type="button"
            onClick={() => onOpenVideo?.()}
            className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full border border-succession-border text-succession-text-secondary hover:text-succession-primary hover:border-succession-primary hover:bg-succession-primary/5 transition-all"
          >
            <PlayIcon className="w-3 h-3" />
            <span>{intl.formatMessage({ id: 'succession.launchpad.welcome_video_rewatch' })}</span>
          </button>
        )}
      </div>
      <div className={tallVariantEnabled ? 'mt-2 space-y-3' : 'mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3'}>
        {activities.map((a, index) => {
          // Video task — visually matches the surrounding quickstart cards.
          // We render a different layout for the tall stepper variant vs the
          // 2-col grid, mirroring GettingStartedStepCard / GettingStartedMiniCard
          // respectively. The only behavioural difference vs the standard cards
          // is `onClick` opens the dialog instead of navigating.
          if (a.id === VIDEO_TASK_ID || a.videoTask) {
            const watched = isActivityCompleted(a.id);
            const iconTileCls = watched
              ? 'bg-succession-success/15 text-succession-success'
              : 'bg-succession-info/10 text-succession-info';
            const rowCls = watched
              ? 'bg-succession-success-bg border-succession-success'
              : 'bg-succession-bg-tertiary border-succession-border hover:border-succession-success hover:bg-succession-hover-bg cursor-pointer';
            const openHandler = () => onOpenVideo?.();
            const keyHandler = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openHandler(); } };

            // Tall stepper variant — mirrors GettingStartedStepCard:
            //   [status circle: step # or check] [icon tile] [title + description]
            if (tallVariantEnabled) {
              const statusCircle = watched ? (
                <div className="w-8 h-8 rounded-full bg-succession-success/15 text-succession-success flex items-center justify-center">
                  <CheckCircleSolidIcon className="w-5 h-5" aria-hidden="true" />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full bg-succession-primary/10 text-succession-primary border-2 border-succession-primary/20 flex items-center justify-center text-sm font-semibold">
                  {index + 1}
                </div>
              );
              return (
                <div
                  key={a.id}
                  className={`${rowCls} relative flex items-center gap-4 p-4 rounded-lg border transition-all`}
                  role="button"
                  tabIndex={0}
                  onClick={openHandler}
                  onKeyDown={keyHandler}
                >
                  <div className="flex-shrink-0">{statusCircle}</div>
                  <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${iconTileCls}`}>
                    <PlayCircleOutlineIcon className="w-5 h-5" aria-hidden="true" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-succession-text-primary leading-tight truncate">{a.title}</h4>
                    <p className="mt-0.5 text-sm text-succession-text-secondary leading-snug line-clamp-1">{a.description}</p>
                  </div>
                </div>
              );
            }

            // 2-col grid variant — mirrors GettingStartedMiniCard:
            //   compact horizontal with completion circle in the top-right
            return (
              <div key={a.id} className="h-24 sm:h-28">
                <div
                  className={`${rowCls} rounded-xl border h-full w-full flex flex-col overflow-hidden transition-all duration-200 hover:shadow-succession-md relative`}
                  role="button"
                  tabIndex={0}
                  onClick={openHandler}
                  onKeyDown={keyHandler}
                  style={{ minWidth: 0 }}
                >
                  <div className="absolute top-2 right-2 z-10">
                    {watched ? (
                      <CheckCircleSolidIcon className="h-5 w-5 text-succession-success" aria-hidden="true" />
                    ) : (
                      <div className="h-5 w-5 rounded-full border-2 border-succession-border" />
                    )}
                  </div>
                  <div className="px-3 py-2 flex items-center gap-3 flex-1 min-w-0">
                    <div className={`shrink-0 rounded-md p-1.5 ${iconTileCls}`}>
                      <PlayCircleOutlineIcon className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-m font-semibold text-succession-text-primary truncate">{a.title}</div>
                      <div className="gs-desc !text-[11px] sm:!text-[12px] md:!text-sm leading-snug text-succession-text-secondary line-clamp-1">
                        {a.description}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          const approvalState = currentUser?.approval_state;
          const isPendingOrDenied =
            approvalState === 'pending' || approvalState === 0 ||
            approvalState === 'denied' || approvalState === 2;
          const approvalProcessEnabled = Array.isArray(features) && (
            features.includes('successor_approval_process') ||
            features.includes('seller_approval_process') ||
            features.includes('simple_marketplace_approval')
          );
          const isLocked = a.id === 'discovery' && approvalProcessEnabled && isPendingOrDenied;
          const lockedLabelDefault = approvalState === 'denied' || approvalState === 2 ? 'Approval Required' : 'Pending Approval';

          if (tallVariantEnabled) {
            return (
              <GettingStartedStepCard
                key={a.id}
                activity={a}
                completed={isActivityCompleted(a.id)}
                locked={isLocked}
                lockedLabel="Locked"
                stepNumber={index + 1}
              />
            );
          }

          return (
            <div key={a.id} className="h-24 sm:h-28">
              <GettingStartedMiniCard
                activity={a}
                completed={isActivityCompleted(a.id)}
                badgeLabel={badgeLabelForId(a.id)}
                locked={isLocked}
                lockedLabel={lockedLabelDefault}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { CatastrophicPlanCBWidget };
export default LaunchPad;


